import React, { useContext, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { isComponentVisible } from "../utils/SaitUtils";
import { useTheme } from "@mui/material/styles";
import { ModuleContext, saitfecoreNs } from "../utils/Constants";
import i18next from "i18next";
import { Box, IconButton, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import PropTypes from "prop-types";

const COPY_FEEDBACK_DURATION_MS = 1500;

const SYNTAX_COLORS_LIGHT = {
	text: "#24292e",
	comment: "#6a737d",
	tag: "#22863a",
	attrName: "#6f42c1",
	attrValue: "#032f62",
	jsonKey: "#005cc5",
	jsonString: "#032f62",
	jsonNumber: "#e36209",
	jsonKeyword: "#005cc5",
	punctuation: "#24292e",
};

const SYNTAX_COLORS_DARK = {
	text: "#c9d1d9",
	comment: "#8b949e",
	tag: "#7ee787",
	attrName: "#d2a8ff",
	attrValue: "#a5d6ff",
	jsonKey: "#79c0ff",
	jsonString: "#a5d6ff",
	jsonNumber: "#ffa657",
	jsonKeyword: "#79c0ff",
	punctuation: "#c9d1d9",
};

function getSyntaxColors(mode) {
	return mode === "dark" ? SYNTAX_COLORS_DARK : SYNTAX_COLORS_LIGHT;
}

// Riconosce il linguaggio del testo in base al contenuto, per poterlo formattare/colorare di conseguenza
function detectLanguage(text) {
	const trimmed = text.trim();
	if (trimmed.startsWith("<")) return "xml";
	if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
		try {
			JSON.parse(trimmed);
			return "json";
		} catch {
			return "text";
		}
	}
	return "text";
}

// Beautifier XML: rimuove l'indentazione esistente e la ricalcola in base all'annidamento dei tag
function formatXml(xml) {
	const collapsed = xml.replace(/>\s*</g, "><").trim();
	const lines = collapsed.replace(/></g, ">\n<").split("\n");

	const tab = "  ";
	let indent = 0;
	const output = [];

	lines.forEach((rawLine) => {
		const line = rawLine.trim();
		if (!line) return;

		const isClosingTag = /^<\/[^>]+>$/.test(line);
		const isSelfClosing = /\/>\s*$/.test(line) || /^<\?.*\?>$/.test(line) || /^<!--.*-->$/.test(line);
		const isOpeningWithClosing = /^<([\w:.-]+)[^>]*>.*<\/\1>$/.test(line);
		const isOpeningTag = !isSelfClosing && /^<[^/!?][^>]*[^/]>$/.test(line);

		if (isClosingTag && indent > 0) {
			indent--;
		}

		output.push(tab.repeat(indent) + line);

		if (isOpeningTag && !isOpeningWithClosing) {
			indent++;
		}
	});

	return output.join("\n");
}

function formatJson(text) {
	return JSON.stringify(JSON.parse(text), null, 2);
}

// Colora una riga XML già formattata: tag, attributi, valori e commenti
function highlightXmlLine(line, key, colors) {
	const regex = /(<!--[\s\S]*?-->)|(<\/?[\w:.-]*)|([\w:.-]+)(\s*=\s*)("[^"]*"|'[^']*')|(\/?>)/g;
	const nodes = [];
	let lastIndex = 0;
	let match;
	let tokenKey = 0;

	while ((match = regex.exec(line)) !== null) {
		if (match.index > lastIndex) {
			nodes.push(line.slice(lastIndex, match.index));
		}

		if (match[1] !== undefined) {
			nodes.push(
				<span key={tokenKey++} style={{ color: colors.comment }}>
					{match[1]}
				</span>,
			);
		} else if (match[2] !== undefined) {
			nodes.push(
				<span key={tokenKey++} style={{ color: colors.tag }}>
					{match[2]}
				</span>,
			);
		} else if (match[3] !== undefined) {
			nodes.push(
				<React.Fragment key={tokenKey++}>
					<span style={{ color: colors.attrName }}>{match[3]}</span>
					<span>{match[4]}</span>
					<span style={{ color: colors.attrValue }}>{match[5]}</span>
				</React.Fragment>,
			);
		} else if (match[6] !== undefined) {
			nodes.push(
				<span key={tokenKey++} style={{ color: colors.tag }}>
					{match[6]}
				</span>,
			);
		}

		lastIndex = regex.lastIndex;
	}

	if (lastIndex < line.length) {
		nodes.push(line.slice(lastIndex));
	}

	return <Box key={key}>{nodes.length ? nodes : "\u00A0"}</Box>;
}

// Colora una riga JSON già formattata: chiavi, stringhe, numeri, booleani/null e punteggiatura
function highlightJsonLine(line, key, colors) {
	const regex = /("(?:\\.|[^"\\])*"(\s*:)?)|(-?\d+(\.\d+)?([eE][+-]?\d+)?)|(true|false|null)|([{}[\],])/g;
	const nodes = [];
	let lastIndex = 0;
	let match;
	let tokenKey = 0;

	while ((match = regex.exec(line)) !== null) {
		if (match.index > lastIndex) {
			nodes.push(line.slice(lastIndex, match.index));
		}

		if (match[1] !== undefined) {
			const isKey = !!match[2];
			nodes.push(
				<span key={tokenKey++} style={{ color: isKey ? colors.jsonKey : colors.jsonString }}>
					{match[1]}
				</span>,
			);
		} else if (match[3] !== undefined) {
			nodes.push(
				<span key={tokenKey++} style={{ color: colors.jsonNumber }}>
					{match[3]}
				</span>,
			);
		} else if (match[6] !== undefined) {
			nodes.push(
				<span key={tokenKey++} style={{ color: colors.jsonKeyword }}>
					{match[6]}
				</span>,
			);
		} else if (match[7] !== undefined) {
			nodes.push(
				<span key={tokenKey++} style={{ color: colors.punctuation }}>
					{match[7]}
				</span>,
			);
		}

		lastIndex = regex.lastIndex;
	}

	if (lastIndex < line.length) {
		nodes.push(line.slice(lastIndex));
	}

	return <Box key={key}>{nodes.length ? nodes : "\u00A0"}</Box>;
}

SaitCodeField.propTypes = {
	objmod: PropTypes.any,
	hidden: PropTypes.bool,
	text: PropTypes.string,
	language: PropTypes.string,
};

/**
 * @param {{
 *  objmod?: any,
 *  hidden?: boolean,
 *  text?: string,
 *  language?: string
 * }} props
 */
export default function SaitCodeField(props) {
	const theme = useTheme();
	const syntaxColors = useMemo(() => getSyntaxColors(theme.palette.mode), [theme.palette.mode]);
	const [copied, setCopied] = useState(false);

	const componentModule = useContext(ModuleContext);
	const objModArray = useSelector((state) => state["auth"]["objMod"]);
	const objMod = objModArray.find((objModObject) => objModObject.moduleName === componentModule && objModObject.idObject === props.objmod);

	const { formattedText, language } = useMemo(() => {
		if (!props.text) return { formattedText: "", language: "text" };

		const detectedLanguage = props.language || detectLanguage(props.text);
		try {
			if (detectedLanguage === "xml") {
				return { formattedText: formatXml(props.text), language: "xml" };
			}
			if (detectedLanguage === "json") {
				return { formattedText: formatJson(props.text), language: "json" };
			}
		} catch {
			// testo non formattabile: viene mostrato così com'è
		}
		return { formattedText: props.text, language: "text" };
	}, [props.text, props.language]);

	const lines = props.text ? formattedText.split("\n") : [];

	return isComponentVisible(objMod, props.hidden) ? (
		<>
			{props.text && (
				<Box
					sx={{
						position: "relative",
						display: "flex",
						justifyContent: "flex-end",
						mr: 0.5,
					}}
				>
					<Tooltip title={i18next.t("copy", { ns: saitfecoreNs })}>
						<IconButton
							size="medium"
							onClick={() => {
								navigator.clipboard.writeText(formattedText);
								setCopied(true);
								setTimeout(() => setCopied(false), COPY_FEEDBACK_DURATION_MS);
							}}
							sx={{
								position: "absolute",
								top: 6,
								right: 8,
								zIndex: 2,
								opacity: copied ? 1 : 0.65,
								backgroundColor: "transparent",
								"&:hover": {
									opacity: 1,
									backgroundColor: theme.palette.action.hover,
								},
							}}
						>
							{copied ? <CheckIcon fontSize="small" sx={{ color: theme.palette.success.main }} /> : <ContentCopyIcon fontSize="small" />}
						</IconButton>
					</Tooltip>
				</Box>
			)}
			<Box
				sx={{
					display: "flex",
					fontFamily: 'Consolas, Monaco, "Courier New", monospace',
					fontSize: "0.8rem",
					lineHeight: 1.5,
					backgroundColor: theme.palette.background.paper,
					border: `1px solid ${theme.palette.divider}`,
					borderRadius: 1,
					overflow: "auto",
				}}
			>
				{/* Numeri di riga */}
				<Box
					sx={{
						px: 1.5,
						py: 1,
						backgroundColor: theme.palette.action.hover,
						color: theme.palette.text.secondary,
						textAlign: "right",
						userSelect: "none",
						borderRight: `1px solid ${theme.palette.divider}`,
						position: "sticky",
						left: 0,
						zIndex: 1,
						flexShrink: 0,
					}}
				>
					{lines.map((_, i) => (
						<Box key={i}>{i + 1}</Box>
					))}
				</Box>
				{/* Contenuto formattato ed evidenziato in base al linguaggio rilevato (XML, JSON, testo semplice) */}
				<Box
					sx={{
						p: 1,
						px: 2,
						color: syntaxColors.text,
						whiteSpace: "pre",
						flexGrow: 1,
					}}
				>
					{lines.map((line, i) =>
						language === "xml" ? (
							highlightXmlLine(line, i, syntaxColors)
						) : language === "json" ? (
							highlightJsonLine(line, i, syntaxColors)
						) : (
							<Box key={i}>{line || "\u00A0"}</Box>
						),
					)}
				</Box>
			</Box>
		</>
	) : null;
}
